import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const POPULAR_LOCALES = [
  ["de_DE", "Deutsch"],
  ["en_US", "Englisch (US)"],
  ["en_GB", "Englisch (UK)"],
  ["fr_FR", "Französisch"],
  ["it_IT", "Italienisch"],
  ["es_ES", "Spanisch"],
  ["nl_NL", "Niederländisch"],
  ["pl_PL", "Polnisch"],
  ["pt_PT", "Portugiesisch"],
  ["cs_CZ", "Tschechisch"],
  ["sv_SE", "Schwedisch"],
  ["da_DK", "Dänisch"],
];

export function PdfGenerator() {
  const [available, setAvailable] = useState<string[]>([]);
  const [sku, setSku] = useState("");
  const [lang, setLang] = useState("de_DE");
  const [search, setSearch] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Produktliste = alle SKUs mit PDF-Vorlage (Block-Map). Seit T-012 ohne UnoPIM.
  useEffect(() => {
    void api
      .get<{ available: string[] }>("/api/pdf/_blockmaps")
      .then((r) => setAvailable(r.available))
      .catch((e) => setError(String(e)));
  }, []);

  const products = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? available.filter((s) => s.includes(q)) : available;
  }, [available, search]);

  async function generate() {
    if (!sku) return;
    setBusy(true);
    setError(null);
    setPreviewUrl(null);
    try {
      const r = await fetch(`/api/pdf/${encodeURIComponent(sku)}?lang=${encodeURIComponent(lang)}`, {
        credentials: "same-origin",
      });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try {
          const body = (await r.json()) as { error?: string; hint?: string; available?: string[] };
          if (body.hint) detail = body.hint;
          else if (body.error) detail = body.error;
        } catch {
          /* keep status */
        }
        throw new Error(detail);
      }
      const blob = await r.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid grid-cols-[320px_1fr] gap-6 h-[calc(100vh-200px)]">
      <aside className="rounded-xl border border-border bg-card p-4 overflow-y-auto">
        <h2 className="font-semibold mb-3 text-sm">Produkt</h2>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="SKU suchen…"
          className="w-full mb-3 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        />
        <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1">
          {products.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setSku(p)}
              className={`w-full text-left px-3 py-1.5 text-sm rounded-md border ${
                sku === p
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-transparent hover:bg-secondary"
              }`}
            >
              <div className="font-medium">{p}</div>
            </button>
          ))}
          {products.length === 0 && (
            <div className="text-xs text-muted-foreground py-2 px-3">Keine Treffer.</div>
          )}
        </div>

        <h2 className="font-semibold mt-5 mb-2 text-sm">Sprache</h2>
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          {POPULAR_LOCALES.map(([code, label]) => (
            <option key={code} value={code}>
              {label} ({code})
            </option>
          ))}
        </select>


        <button
          type="button"
          disabled={!sku || busy}
          onClick={generate}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" />
              Generiere…
            </>
          ) : (
            <>
              <i className="fa-solid fa-file-pdf" />
              PDF generieren
            </>
          )}
        </button>

        {previewUrl && (
          <a
            href={previewUrl}
            download={`${sku}_${lang}.pdf`}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-secondary"
          >
            <i className="fa-solid fa-download" />
            Download
          </a>
        )}

        {error && <p className="mt-3 text-xs text-destructive break-words">{error}</p>}
      </aside>

      <section className="rounded-xl border border-border bg-card overflow-hidden">
        {previewUrl ? (
          <iframe
            src={previewUrl}
            title="PDF-Vorschau"
            className="w-full h-full bg-white"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
            <div className="text-center max-w-md p-8">
              <i className="fa-regular fa-file-pdf text-5xl text-primary/40 mb-4" />
              <p>Wähle ein Produkt + eine Sprache aus, dann klick <b>PDF generieren</b>.</p>
              <p className="mt-2 text-xs">
                Übersetzungen laufen über Claude Haiku 4.5 und werden im Translation-Memory
                gecacht — beim zweiten Mal kommt das PDF in Sekunden.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
